import type { ReactNode } from 'react';
import React, { useState, Children } from 'react';
import { Box, Collapse, Typography, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface NavigationSectionProps {
  id: string;
  label: string;
  defaultExpanded?: boolean;
  sideBarOpen: boolean;
  children: ReactNode;
}

const storageKey = (id: string) => `nav-section:${id}`;

export default function NavigationSection({
  id,
  label,
  defaultExpanded = true,
  sideBarOpen,
  children
}: NavigationSectionProps): JSX.Element | null {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(storageKey(id));
      if (stored === 'expanded') return true;
      if (stored === 'collapsed') return false;
    } catch {
      // ignore storage failures
    }
    return defaultExpanded;
  });

  // Children.toArray strips null/undefined/booleans, so length === 0 means
  // every child was gated out by role checks — render nothing.
  if (Children.toArray(children).length === 0) {
    return null;
  }

  const toggle = () => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey(id), next ? 'expanded' : 'collapsed');
      } catch {
        // ignore storage failures
      }
      return next;
    });
  };

  // When the rail is collapsed (icon-only), hide sectioned items entirely —
  // only the top-level items rendered outside any section remain visible.
  if (!sideBarOpen) {
    return null;
  }

  return (
    <Box>
      <Box
        component="button"
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={`${id}-content`}
        sx={(t) => ({
          display: 'flex',
          alignItems: 'center',
          gap: t.spacing(1),
          width: '100%',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: t.spacing(0.75, 2, 0.75, 1.5),
          color: t.palette.text.primary,
          borderRadius: '6px',
          '&:hover': {
            backgroundColor: t.palette.action.hover
          },
          '&:focus-visible': {
            outline: `2px solid ${t.palette.primary.main}`,
            outlineOffset: 2
          }
        })}
      >
        {expanded ? <ExpandMoreIcon sx={{ fontSize: '1.5rem' }} /> : <ChevronRightIcon sx={{ fontSize: '1.5rem' }} />}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            lineHeight: 1.5
          }}
        >
          {label}
        </Typography>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
        <Box
          id={`${id}-content`}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
            mt: theme.spacing(1)
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}
